import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Filter, Upload, Receipt, Download, Trash2, MessageCircle, AlertTriangle, Check, CheckCheck } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { PageHeader } from '../components/PageHeader';
import { PeriodPicker, useSharedPeriod, periodToParams } from '../components/PeriodPicker';
import { api, fmtDate, fmtDateTime, fmtMoney } from '../lib/api';

export default function BillsPage() {
    const [bills, setBills] = useState(null);
    const [vendors, setVendors] = useState([]);
    const [q, setQ] = useState('');
    const [vendor, setVendor] = useState('all');   // stores the vendor_key (or "all")
    const [period, setPeriod] = useSharedPeriod();
    const [sortBy, setSortBy] = useState('uploaded');   // uploaded | date | amount | vendor
    const [sortDir, setSortDir] = useState('desc');
    const [confirmDelete, setConfirmDelete] = useState(null); // bill object or null
    const [deleting, setDeleting] = useState(false);
    // Sprint 2026-07 (B): review-queue tab + review count. `tab` drives the
    // ``review`` query param passed to /api/bills; the count is polled so
    // the badge stays fresh even without a full reload.
    const [tab, setTab] = useState('all');   // 'all' | 'review'
    const [reviewCount, setReviewCount] = useState(0);
    const [approving, setApproving] = useState(false);

    async function load() {
        const params = {};
        if (q) params.q = q;
        // Send vendor_key (stable) rather than the display name (may not
        // match after a merge where display and key intentionally differ).
        if (vendor && vendor !== 'all') params.vendor_key = vendor;
        // Period filter (Sprint 2026-01 unified filter).
        Object.assign(params, periodToParams(period));
        if (sortBy) params.sort_by = sortBy;
        if (sortDir) params.sort_dir = sortDir;
        // Sprint 2026-07 (B): review-tab narrows the list to needs_review=true.
        if (tab === 'review') params.review = 'pending';
        const [b, v, rc] = await Promise.all([
            api.listBills(params),
            api.listVendors(),
            api.billsReviewCount().catch(() => ({ count: 0 })),
        ]);
        setBills(b);
        setVendors(v);
        setReviewCount(rc?.count || 0);
    }

    useEffect(() => {
        load(); // eslint-disable-next-line
    }, [vendor, period?.from, period?.to, period?.preset, sortBy, sortDir, tab]);

    async function download(b) {
        try {
            await api.downloadBillFile(b.id, b.original_filename || 'bill');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Download failed');
        }
    }

    async function handleDelete() {
        if (!confirmDelete) return;
        setDeleting(true);
        try {
            await api.deleteBill(confirmDelete.id);
            toast.success(`Bill from ${confirmDelete.vendor || 'vendor'} removed`);
            setConfirmDelete(null);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to delete bill');
        } finally {
            setDeleting(false);
        }
    }

    async function approveOne(billId) {
        try {
            await api.markBillReviewed(billId);
            toast.success('Marked as reviewed');
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to mark as reviewed');
        }
    }

    async function approveAll() {
        if (!filtered.length) return;
        setApproving(true);
        try {
            const ids = filtered.filter((b) => b.needs_review).map((b) => b.id);
            if (!ids.length) {
                toast.info('No bills in this list need review.');
                return;
            }
            const res = await api.markBillsReviewedBulk(ids);
            toast.success(`Approved ${res.modified || 0} bill${res.modified === 1 ? '' : 's'}`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Bulk approve failed');
        } finally {
            setApproving(false);
        }
    }

    const filtered = bills || [];

    return (
        <div className="space-y-5 animate-fade-in">
            <PageHeader
                title="Bills"
                subtitle={
                    tab === 'review'
                        ? `${reviewCount} bill${reviewCount === 1 ? '' : 's'} flagged for your review — the OCR wasn't fully confident. Approve the ones that look right, or open individual bills to fix.`
                        : period && period.preset !== 'all'
                            ? `Showing bills from ${period.label}. Change period or filter by vendor.`
                            : 'All scanned bills. Filter by vendor or search across vendors, invoice numbers and items.'
                }
                actions={
                    <div className="flex items-center gap-2">
                        <PeriodPicker value={period} onChange={setPeriod} testid="bills-period-picker" />
                        <Link to="/upload">
                            <Button className="gap-2">
                                <Upload size={16} /> Upload Bill
                            </Button>
                        </Link>
                    </div>
                }
            />

            {/* Sprint 2026-07 (B): review-queue tab. Splits the page into
                "All bills" and "Needs review" so the OCR-flagged uploads
                have a dedicated inbox. Badge shows live pending count. */}
            <Tabs value={tab} onValueChange={setTab} className="w-full">
                <TabsList data-testid="bills-tabs">
                    <TabsTrigger value="all" data-testid="bills-tab-all">All bills</TabsTrigger>
                    <TabsTrigger value="review" data-testid="bills-tab-review" className="gap-2">
                        Needs review
                        {reviewCount > 0 && (
                            <Badge variant="destructive" className="px-1.5 py-0 h-5 text-[10px] font-mono tabular-nums">
                                {reviewCount}
                            </Badge>
                        )}
                    </TabsTrigger>
                </TabsList>
            </Tabs>

            {tab === 'review' && filtered.some((b) => b.needs_review) && (
                <Card className="p-3 border-amber-500/40 bg-amber-500/5 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 text-sm">
                        <AlertTriangle size={16} className="text-amber-600" />
                        <span>
                            <span className="font-medium">{filtered.filter((b) => b.needs_review).length}</span>{' '}
                            bill{filtered.filter((b) => b.needs_review).length === 1 ? '' : 's'} waiting for review.
                        </span>
                    </div>
                    <Button
                        size="sm"
                        variant="default"
                        disabled={approving}
                        onClick={approveAll}
                        data-testid="bills-approve-all"
                        className="gap-1.5"
                    >
                        <CheckCheck size={14} /> Approve all
                    </Button>
                </Card>
            )}

            <Card className="p-4 border-border/80">
                <div className="flex flex-col md:flex-row gap-3 md:items-end">
                    <div className="flex-1">
                        <label className="text-xs text-muted-foreground">Search</label>
                        <div className="relative">
                            <Search
                                size={14}
                                className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                            />
                            <Input
                                data-testid="bills-search-input"
                                placeholder="Vendor, invoice #, or item…"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && load()}
                                className="pl-8"
                            />
                        </div>
                    </div>
                    <div className="md:w-64">
                        <label className="text-xs text-muted-foreground">Vendor</label>
                        <Select value={vendor} onValueChange={setVendor}>
                            <SelectTrigger data-testid="bills-vendor-filter">
                                <SelectValue placeholder="All vendors" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All vendors</SelectItem>
                                {vendors.map((v) => (
                                    <SelectItem key={v.vendor_key} value={v.vendor_key}>
                                        {v.vendor}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="md:w-56">
                        <label className="text-xs text-muted-foreground">Sort by</label>
                        <Select value={`${sortBy}:${sortDir}`} onValueChange={(v) => {
                            const [b, d] = v.split(':');
                            setSortBy(b); setSortDir(d);
                        }}>
                            <SelectTrigger data-testid="bills-sort">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="uploaded:desc">Uploaded (newest)</SelectItem>
                                <SelectItem value="uploaded:asc">Uploaded (oldest)</SelectItem>
                                <SelectItem value="date:desc">Bill date (newest)</SelectItem>
                                <SelectItem value="date:asc">Bill date (oldest)</SelectItem>
                                <SelectItem value="amount:desc">Amount (high → low)</SelectItem>
                                <SelectItem value="amount:asc">Amount (low → high)</SelectItem>
                                <SelectItem value="vendor:asc">Vendor (A → Z)</SelectItem>
                                <SelectItem value="vendor:desc">Vendor (Z → A)</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <Button variant="outline" onClick={load} data-testid="bills-apply-filters" className="gap-2">
                        <Filter size={14} /> Apply
                    </Button>
                </div>
            </Card>

            <Card className="p-0 border-border/80 overflow-hidden">
                {bills === null ? (
                    <div className="p-4 space-y-2">
                        {Array.from({ length: 6 }).map((_, i) => (
                            <Skeleton key={i} className="h-10" />
                        ))}
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="p-10 text-center">
                        <div className="mx-auto h-10 w-10 rounded-full bg-accent text-accent-foreground flex items-center justify-center">
                            <Receipt size={20} />
                        </div>
                        <div className="font-heading text-lg mt-3">No bills match</div>
                        <div className="text-sm text-muted-foreground">
                            Try changing your filters or upload a new bill.
                        </div>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="bills-table">
                            <thead className="bg-muted/40 text-muted-foreground">
                                <tr>
                                    <th className="text-left px-4 py-2 font-medium">Vendor</th>
                                    <th className="text-left px-4 py-2 font-medium">Invoice #</th>
                                    <th className="text-left px-4 py-2 font-medium">Bill date</th>
                                    <th className="text-left px-4 py-2 font-medium">Uploaded</th>
                                    <th className="text-right px-4 py-2 font-medium">Items</th>
                                    <th className="text-right px-4 py-2 font-medium">Total</th>
                                    <th className="text-right px-4 py-2 font-medium">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((b) => (
                                    <tr key={b.id} className="border-t border-border hover:bg-muted/40">
                                        <td className="px-4 py-2">
                                            <Link
                                                to={`/bills/${b.id}`}
                                                data-testid={`bills-row-${b.id}`}
                                                className="hover:underline uppercase tracking-wide"
                                            >
                                                {b.title || b.vendor || 'Unknown'}
                                            </Link>
                                            {b.title && b.vendor && (
                                                <div className="text-[11px] text-muted-foreground uppercase" data-testid={`bills-vendor-sub-${b.id}`}>
                                                    {b.vendor}
                                                </div>
                                            )}
                                            {b.extracted_by && (
                                                <span
                                                    className="ml-2 inline-flex items-center rounded-full border border-border bg-muted/50 px-1.5 py-0 text-[9px] uppercase tracking-wide align-middle"
                                                    title="Extracted by AI"
                                                >
                                                    AI
                                                </span>
                                            )}
                                            {b.source === 'whatsapp' && (
                                                <span
                                                    className="ml-1 inline-flex items-center gap-1 rounded-full border border-emerald-300 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950 px-1.5 py-0 text-[9px] uppercase tracking-wide text-emerald-700 dark:text-emerald-400 align-middle"
                                                    title={`Received via WhatsApp${b.source_phone ? ` from ${b.source_phone}` : ''}`}
                                                    data-testid={`bill-source-whatsapp-${b.id}`}
                                                >
                                                    <MessageCircle size={9} />
                                                    WhatsApp
                                                </span>
                                            )}
                                            {b.needs_review && (
                                                <span
                                                    className="ml-1 inline-flex items-center gap-1 rounded-full border border-amber-400 dark:border-amber-700 bg-amber-50 dark:bg-amber-950 px-1.5 py-0 text-[9px] uppercase tracking-wide text-amber-700 dark:text-amber-400 align-middle"
                                                    title={
                                                        (b.review_reasons || []).length
                                                            ? `Needs review: ${(b.review_reasons || []).join(', ')}`
                                                            : 'This bill needs your review'
                                                    }
                                                    data-testid={`bill-needs-review-${b.id}`}
                                                >
                                                    <AlertTriangle size={9} />
                                                    Review
                                                </span>
                                            )}
                                            {b.vendor_key && (
                                                <div>
                                                    <Link
                                                        to={`/vendors/${encodeURIComponent(b.vendor_key)}`}
                                                        className="text-xs text-muted-foreground hover:underline"
                                                    >
                                                        vendor account →
                                                    </Link>
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-4 py-2 text-muted-foreground">{b.invoice_number || '—'}</td>
                                        <td className="px-4 py-2 text-muted-foreground">{fmtDate(b.bill_date)}</td>
                                        <td className="px-4 py-2 text-muted-foreground">{fmtDateTime(b.uploaded_at)}</td>
                                        <td className="px-4 py-2 text-right tabular-nums font-mono">{(b.items || []).length}</td>
                                        <td className="px-4 py-2 text-right tabular-nums font-mono">{fmtMoney(b.total, b.currency)}</td>
                                        <td className="px-4 py-2 text-right">
                                            <div className="inline-flex items-center gap-1 justify-end">
                                                {b.needs_review && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="gap-1 text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-950/30"
                                                        onClick={() => approveOne(b.id)}
                                                        data-testid={`bills-approve-${b.id}`}
                                                        aria-label="Mark as reviewed"
                                                        title="Approve — mark as reviewed"
                                                    >
                                                        <Check size={14} />
                                                    </Button>
                                                )}
                                                {b.file_id ? (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="gap-1"
                                                        onClick={() => download(b)}
                                                        data-testid={`bills-download-${b.id}`}
                                                        aria-label="Download original file"
                                                        title="Download original file"
                                                    >
                                                        <Download size={14} />
                                                    </Button>
                                                ) : null}
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="gap-1 text-[hsl(var(--price-increase-fg))] hover:bg-[hsl(var(--price-increase-bg))]"
                                                    onClick={() => setConfirmDelete(b)}
                                                    data-testid={`bills-delete-${b.id}`}
                                                    aria-label="Delete bill"
                                                    title="Delete bill"
                                                >
                                                    <Trash2 size={14} />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
                <AlertDialogContent data-testid="bills-delete-confirm-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this bill?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will permanently remove the bill from{' '}
                            <span className="font-medium text-foreground">
                                {confirmDelete?.vendor || 'this vendor'}
                            </span>{' '}
                            dated {confirmDelete ? fmtDate(confirmDelete.bill_date) : ''}{' '}
                            (total {confirmDelete ? fmtMoney(confirmDelete.total, confirmDelete.currency) : ''}).
                            Any price alerts derived from it will also be removed. This cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => {
                                e.preventDefault();
                                handleDelete();
                            }}
                            disabled={deleting}
                            data-testid="bills-delete-confirm-btn"
                        >
                            {deleting ? 'Deleting…' : 'Delete bill'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
